/**
 * Document-title formatting.
 *
 * Today the SPA has a single static `<title>` in apps/web/index.html and no
 * `document.title` setter anywhere — so per-page titles are NEW capability
 * (plan §5 Phase 1.6). Phase 1 only swaps the product name in; wiring
 * per-route page names is left to whoever needs it, using `formatPageTitle`.
 *
 * Substitution is literal string replacement, never evaluation. Templates come
 * from brand config, which is platform-admin-authored and zod-validated, but
 * treating them as data rather than code keeps that from mattering.
 *
 * No React, no Node.
 */
import type { BrandSnapshot } from './snapshot.js'

/** Replace `{placeholder}` occurrences from a value map. Unknown placeholders
 *  are left as-is rather than blanked, so a template typo is visible in the
 *  title instead of silently producing `" · "`. */
function interpolate(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    Object.prototype.hasOwnProperty.call(values, key) ? values[key]! : match,
  )
}

/**
 * The app-root title, used when no specific page name applies.
 *
 * For `DEFAULT_BRAND` this returns exactly `Papan - Any file is a database`,
 * byte-identical to the static `<title>` in index.html. That equality is
 * asserted in the unit tests and is what makes Phase 1 invisible on the
 * default deployment.
 */
export function formatRootTitle(snapshot: BrandSnapshot): string {
  return interpolate(snapshot.identity.rootTitleTemplate, {
    product: snapshot.identity.productName,
    tagline: snapshot.identity.tagline,
  })
}

/**
 * Title for a named page, e.g. `formatPageTitle(brand, 'Settings')` →
 * `Settings · Papan`.
 *
 * An empty/whitespace-only page name falls through to `formatRootTitle`, so
 * callers can pass a possibly-absent route label without branching.
 */
export function formatPageTitle(snapshot: BrandSnapshot, page?: string | null): string {
  const trimmed = page?.trim()
  if (!trimmed) return formatRootTitle(snapshot)
  return interpolate(snapshot.identity.pageTitleTemplate, {
    page: trimmed,
    product: snapshot.identity.productName,
    tagline: snapshot.identity.tagline,
  })
}
